import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLogin } from '../../auth/useLogin'
import { trackLogin, trackLogout, resetAnalytics } from '../../analytics/posthog'
import { useT, useI18n } from '../../i18n/useT'
import { UI_LOCALES } from '../../i18n/config'
import { Logo } from './Logo'
import { lingvanexUrl } from '../../lib/lingvanex'
import { MobileMenu } from './MobileMenu'

interface HeaderProps {
  credits?: number
  /** Registered free account — its balance is this month's allowance */
  monthlyCredits?: boolean
}

/** Compact globe dropdown — the interface language, independent of the
 * transcription language pickers. */
function LanguageSwitcher() {
  const { locale, setLocale } = useI18n()
  const t = useT()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const current = UI_LOCALES.find((l) => l.code === locale)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-2 text-sm text-text-secondary hover:text-text rounded-lg hover:bg-surface-hover transition-colors"
        aria-label={t('ui.language')}
        aria-expanded={open}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zm0 0c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3 7.5 7.03 7.5 12s2.015 9 4.5 9zM3.6 9h16.8M3.6 15h16.8" />
        </svg>
        <span className="uppercase text-xs font-medium">{current?.code.slice(0, 2) ?? 'en'}</span>
        <svg className={`w-3 h-3 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-48 max-h-80 overflow-y-auto bg-surface border border-border rounded-xl shadow-lg z-50 py-1.5">
          {UI_LOCALES.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLocale(l.code)
                setOpen(false)
              }}
              className={`w-full text-left px-3.5 py-1.5 text-sm transition-colors hover:bg-surface-hover ${
                l.code === locale ? 'text-primary font-medium' : 'text-text'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function Header({ credits, monthlyCredits }: HeaderProps) {
  const { isAuthenticated, user, logout } = useAuth0()
  const login = useLogin()
  const t = useT()
  const { locale } = useI18n()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)

  const navLinks = [
    { label: t('nav.howItWorks'), href: '/#how-it-works' },
    { label: t('nav.pricing'), href: '/#pricing' },
    { label: t('nav.faq'), href: '/#faq' },
  ]
  // The one link to the parent site — new tab, so it never kills an
  // in-progress transcription; lands on the reader's locale
  const lingvanexLink = { label: t('nav.moreFromLingvanex'), href: lingvanexUrl('/', locale), external: true }

  // Tap outside the header closes the mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return
    function handleOutside(e: MouseEvent | TouchEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('touchstart', handleOutside)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('touchstart', handleOutside)
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (!isProfileOpen) return
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isProfileOpen])

  function handleLogin() {
    trackLogin()
    setTimeout(() => login(), 150)
  }

  function handleLogout() {
    trackLogout()
    resetAnalytics()
    // Signing out does not hand back the free trial. The counter used to sit
    // at "2" from this browser's first anonymous visit — nothing ever cleared
    // it but a successful initialize — so logging out advertised two free
    // transcriptions to someone who has an account.
    try {
      localStorage.setItem('stt_upload_count', '0')
    } catch {
      // storage denied — the counter was never written either
    }
    setTimeout(() => logout({ logoutParams: { returnTo: window.location.origin } }), 150)
  }

  return (
    <header ref={headerRef} className="sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0">
            <Logo className="h-7 w-auto text-logo" />
          </a>

          {/* Desktop Nav — full nav needs ~900px with the auth block, so it
              only appears at lg; below that the burger menu takes over */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-2 text-sm whitespace-nowrap text-text-secondary hover:text-text rounded-lg hover:bg-surface-hover transition-colors"
              >
                {link.label}
              </a>
            ))}
            <span className="mx-2 h-4 w-px bg-border" aria-hidden="true" />
            <a
              href={lingvanexLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap text-text-muted hover:text-text rounded-lg hover:bg-surface-hover transition-colors"
            >
              {lingvanexLink.label}
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </a>
          </nav>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href="/history"
              className="px-4 py-2 text-sm font-medium border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              {t('nav.history')}
            </a>
            {isAuthenticated ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors"
                >
                  <div className="w-7 h-7 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-semibold">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm text-text max-w-[140px] truncate">{user?.email}</span>
                  <svg className={`w-3.5 h-3.5 text-text-muted transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isProfileOpen && (
                    <div className="absolute right-0 mt-1 w-56 bg-surface border border-border rounded-xl shadow-lg z-50 p-4">
                      <p className="text-sm font-medium text-text break-words">{user?.email}</p>
                      {credits !== undefined && (
                        <p className="mt-1 text-sm text-text-muted">
                          {monthlyCredits
                            ? credits === 1
                              ? t('credits.remainingMonthlyOne')
                              : t('credits.remainingMonthly', { count: credits })
                            : credits === 1
                              ? t('credits.remainingOne')
                              : t('credits.remaining', { count: credits })}
                        </p>
                      )}
                      <div className="mt-4 flex flex-col gap-2">
                        <button
                          onClick={() => {
                            setIsProfileOpen(false)
                            // #pricing only exists on the landing page — from
                            // /history (or anywhere else) navigate to it instead
                            const pricing = document.getElementById('pricing')
                            if (pricing) {
                              pricing.scrollIntoView({ behavior: 'smooth' })
                            } else {
                              window.location.href = '/#pricing'
                            }
                          }}
                          className="w-full py-2 px-4 text-sm font-medium border border-border rounded-lg hover:bg-surface-hover transition-colors"
                        >
                          {t('nav.plansPricing')}
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full py-2 px-4 text-sm font-medium border border-border rounded-lg hover:bg-surface-hover transition-colors"
                        >
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="px-4 py-2 text-sm font-medium bg-primary text-on-accent rounded-lg hover:bg-primary-hover transition-colors shadow-sm"
              >
                {t('nav.login')}
              </button>
            )}
          </div>

          {/* Mobile: hamburger only — the balance already shows in the
              upload field and inside the menu, a third copy is noise */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
              aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-5 h-5 text-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navLinks={[...navLinks, lingvanexLink]}
        isAuthenticated={isAuthenticated}
        userEmail={user?.email}
        credits={credits}
        monthlyCredits={monthlyCredits}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
    </header>
  )
}
